import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/utils/api';
import { Plus, Megaphone, Users, Eye, Phone, Calendar, TrendingUp } from 'lucide-react';
import Layout from '@/components/Layout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const stageColors = {
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-yellow-100 text-yellow-700',
  viewing: 'bg-purple-100 text-purple-700',
  negotiation: 'bg-orange-100 text-orange-700',
  converted: 'bg-green-100 text-green-700',
  lost: 'bg-gray-100 text-gray-700'
};

export default function Marketing() {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const [leads, setLeads] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterStage, setFilterStage] = useState('all');
  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', source: 'website',
    interested_unit: '', stage: 'new', notes: ''
  });

  useEffect(() => { fetchLeads(); }, []);

  const fetchLeads = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/marketing/leads`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setLeads(await res.json());
    } catch (error) { /* will be empty initially */ }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/marketing/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(formData)
      });
      setDialogOpen(false);
      setFormData({ name: '', phone: '', email: '', source: 'website', interested_unit: '', stage: 'new', notes: '' });
      fetchLeads();
    } catch (error) { console.error('Failed:', error); }
  };

  const updateStage = async (leadId, newStage) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/marketing/leads/${leadId}/stage?stage=${newStage}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchLeads();
    } catch (error) { console.error('Failed:', error); }
  };

  const filteredLeads = filterStage === 'all' ? leads : leads.filter(l => l.stage === filterStage);

  const stats = {
    total: leads.length,
    new: leads.filter(l => l.stage === 'new').length,
    viewing: leads.filter(l => l.stage === 'viewing').length,
    converted: leads.filter(l => l.stage === 'converted').length
  };

  const stages = ['all', 'new', 'contacted', 'viewing', 'negotiation', 'converted', 'lost'];
  const stageLabels = isAr
    ? { all: 'الكل', new: 'جديد', contacted: 'تم التواصل', viewing: 'معاينة', negotiation: 'تفاوض', converted: 'محول', lost: 'خسارة' }
    : { all: 'All', new: 'New', contacted: 'Contacted', viewing: 'Viewing', negotiation: 'Negotiation', converted: 'Converted', lost: 'Lost' };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
              <Megaphone className="h-5 w-5 text-purple-600" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-gray-900">{isAr ? 'التسويق وإدارة العملاء' : 'Marketing & CRM'}</h1>
              <p className="text-sm text-gray-500">{isAr ? 'تتبع العملاء المحتملين وإدارة عمليات التأجير' : 'Track leads and manage leasing pipeline'}</p>
            </div>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gradient-primary text-white rounded-xl"><Plus className="h-4 w-4 me-2" /> {isAr ? 'إضافة عميل' : 'Add Lead'}</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{isAr ? 'إضافة عميل محتمل' : 'Add New Lead'}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>{isAr ? 'الاسم' : 'Name'}*</Label><Input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required /></div>
                  <div><Label>{isAr ? 'الهاتف' : 'Phone'}*</Label><Input value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} required /></div>
                  <div><Label>{isAr ? 'البريد' : 'Email'}</Label><Input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} /></div>
                  <div>
                    <Label>{isAr ? 'المصدر' : 'Source'}</Label>
                    <Select value={formData.source} onValueChange={v => setFormData({ ...formData, source: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="website">{isAr ? 'الموقع' : 'Website'}</SelectItem>
                        <SelectItem value="referral">{isAr ? 'إحالة' : 'Referral'}</SelectItem>
                        <SelectItem value="social">{isAr ? 'وسائل التواصل' : 'Social Media'}</SelectItem>
                        <SelectItem value="walkin">{isAr ? 'زيارة مباشرة' : 'Walk-in'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>{isAr ? 'ملاحظات' : 'Notes'}</Label><Input value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} /></div>
                <Button type="submit" className="w-full gradient-primary text-white rounded-xl">{isAr ? 'حفظ' : 'Save'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <Card className="shadow-soft border-gray-100"><CardContent className="pt-5"><Users className="h-5 w-5 text-blue-600 mb-2" strokeWidth={1.5} /><div className="text-2xl font-heading font-bold">{stats.total}</div><div className="text-xs text-gray-500">{isAr ? 'إجمالي العملاء' : 'Total Leads'}</div></CardContent></Card>
          <Card className="shadow-soft border-gray-100"><CardContent className="pt-5"><Phone className="h-5 w-5 text-yellow-600 mb-2" strokeWidth={1.5} /><div className="text-2xl font-heading font-bold">{stats.new}</div><div className="text-xs text-gray-500">{isAr ? 'جديد' : 'New'}</div></CardContent></Card>
          <Card className="shadow-soft border-gray-100"><CardContent className="pt-5"><Eye className="h-5 w-5 text-purple-600 mb-2" strokeWidth={1.5} /><div className="text-2xl font-heading font-bold">{stats.viewing}</div><div className="text-xs text-gray-500">{isAr ? 'معاينة' : 'Viewing'}</div></CardContent></Card>
          <Card className="shadow-soft border-gray-100"><CardContent className="pt-5"><TrendingUp className="h-5 w-5 text-green-600 mb-2" strokeWidth={1.5} /><div className="text-2xl font-heading font-bold">{stats.converted}</div><div className="text-xs text-gray-500">{isAr ? 'محول' : 'Converted'}</div></CardContent></Card>
        </div>

        {/* Stage Filters */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {stages.map(s => (
            <button key={s} onClick={() => setFilterStage(s)} className={`px-4 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${filterStage === s ? 'bg-primary text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-primary/30'}`}>
              {stageLabels[s]}
            </button>
          ))}
        </div>

        {/* Leads Table */}
        <Card className="shadow-soft border-gray-100">
          <CardContent className="pt-6">
            {filteredLeads.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <Megaphone className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>{isAr ? 'لا يوجد عملاء محتملين بعد' : 'No leads yet'}</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{isAr ? 'الاسم' : 'Name'}</TableHead>
                    <TableHead>{isAr ? 'الهاتف' : 'Phone'}</TableHead>
                    <TableHead>{isAr ? 'المصدر' : 'Source'}</TableHead>
                    <TableHead>{isAr ? 'المرحلة' : 'Stage'}</TableHead>
                    <TableHead>{isAr ? 'الإجراء' : 'Action'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLeads.map(lead => (
                    <TableRow key={lead.id}>
                      <TableCell className="font-medium">{lead.name}</TableCell>
                      <TableCell>{lead.phone}</TableCell>
                      <TableCell className="capitalize">{lead.source}</TableCell>
                      <TableCell><span className={`px-2 py-1 rounded-full text-xs font-medium ${stageColors[lead.stage]}`}>{stageLabels[lead.stage]}</span></TableCell>
                      <TableCell>
                        <Select value={lead.stage} onValueChange={v => updateStage(lead.id, v)}>
                          <SelectTrigger className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {stages.filter(s => s !== 'all').map(s => <SelectItem key={s} value={s}>{stageLabels[s]}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
